import type { TimelineItem } from '@/lib/types';
import type { ActiveSignal } from '@/playback/signals';

/**
 * A signal expressed as the thing the playback loop already knows how to play.
 *
 * The alternative was a second playback path — a signal renderer beside the
 * timeline renderer — and that path would have to re-implement item advance,
 * durations, sequential position, layout loading and transitions, in the part of
 * the product with the least forgiving failure mode. So an override is dressed
 * as a timeline segment instead, and the loop is unchanged except for which
 * segment it is handed (D9).
 *
 * The window is the signal's own: it starts when the signal was raised and ends
 * at its expiry, which means the loop's ordinary "this segment is over" timer is
 * also the failsafe release. The anchor is the raise time, so two screens showing
 * the same interval notice stay in step with each other for the same reason two
 * screens on the same schedule do.
 */
export function overrideSegment(signal: ActiveSignal): TimelineItem | null {
  // A signal whose kind has no configured playlist cannot override anything:
  // there is nothing to put on the screen, and a black foyer board is worse than
  // the programme it would have replaced.
  if (signal.playlistId === null) return null;
  return {
    // Prefixed and derived from the signal id, so the loop can recognise the
    // segment it is already playing and not restart an interval notice every
    // time the timeline is refetched underneath it.
    scheduleId: overrideSegmentId(signal),
    playlistId: signal.playlistId,
    layoutId: null,
    start: new Date(signal.startedAtMs).toISOString(),
    end: new Date(signal.expiresAtMs).toISOString(),
    anchor: new Date(signal.startedAtMs).toISOString(),
  };
}

export function overrideSegmentId(signal: ActiveSignal): string {
  return `signal:${signal.id}`;
}

export function isOverrideSegment(segment: TimelineItem | null): boolean {
  return segment !== null && segment.scheduleId.startsWith('signal:');
}
